import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form as FormikForm } from 'formik';

import {
  TextField,
  TextAreaField,
  DateField,
  SelectFieldV2,
  DisableTextField,
  SubmitButton,
  CancelPopupButton,
} from 'ui/FormElement';
import { useContext } from 'components/Admin/Webinars/Context';
import useTimeZones from 'components/Common/TimeZones/Hooks/useTimeZones';

export default function Form({ row, onHide }) {
  const { dispatch, actions } = useContext();
  const { timeZones } = useTimeZones();

  const FormSchema = Yup.object().shape({
    topic: Yup.string().required('Topic is required'),
    agenda: Yup.string().required('Agenda is required'),
    start_date: Yup.string().required('Start Date is required').nullable(),
    start_time: Yup.string().required('Start Time is required').nullable(),
    time_zone: Yup.string().required('Time Zone is required').nullable(),
    duration: Yup.number().required('Duration is required'),
  });

  const formData = {
    topic: row.attributes.topic,
    agenda: row.attributes.agenda,
    start_date: row.attributes.start_date_formatted,
    start_time: row.attributes.start_time,
    time_zone: row.attributes.time_zone,
    host: row.attributes.host,
    duration: row.attributes.duration,
  };

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    setSubmitting(true);

    actions.update(
      dispatch,
      row.id,
      values,
      () => {
        setSubmitting(false);
        swal('Webinar Updated');
        onHide();
      },
      (errors) => {
        const newErrors = {};
        errors.forEach((item) => {
          newErrors[item.source.pointer.replace('/data/attributes/', '')] = item.detail;
        });
        setErrors(newErrors);
        setSubmitting(false);
      },
    );
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={FormSchema}
      initialValues={formData}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <FormikForm className="needs-validation" noValidate="">
          <TextField
            name="topic"
            label={(
              <>
                <span>Topic</span>
                <abbr title="required">*</abbr>
              </>
            )}
          />

          <TextAreaField
            name="agenda"
            label={(
              <>
                <span>Agenda</span>
                <abbr title="required">*</abbr>
              </>
            )}
          />

          <DateField
            name="start_date"
            label={(
              <>
                <span>Start Date</span>
                <abbr title="required">*</abbr>
              </>
            )}
          />

          <SelectFieldV2
            name="start_time"
            label={(
              <>
                <span>Start Time</span>
                <abbr title="required">*</abbr>
              </>
            )}
            placeholder="Choose Start Time..."
            options={[
              { value: '12:00 AM', label: '12:00 AM' },
              { value: '12:30 AM', label: '12:30 AM' },
              { value: '1:00 AM', label: '1:00 AM' },
              { value: '1:30 AM', label: '1:30 AM' },
              { value: '2:00 AM', label: '2:00 AM' },
              { value: '2:30 AM', label: '2:30 AM' },
              { value: '3:00 AM', label: '3:00 AM' },
              { value: '3:30 AM', label: '3:30 AM' },
              { value: '4:00 AM', label: '4:00 AM' },
              { value: '4:30 AM', label: '4:30 AM' },
              { value: '5:00 AM', label: '5:00 AM' },
              { value: '5:30 AM', label: '5:30 AM' },
              { value: '6:00 AM', label: '6:00 AM' },
              { value: '6:30 AM', label: '6:30 AM' },
              { value: '7:00 AM', label: '7:00 AM' },
              { value: '7:30 AM', label: '7:30 AM' },
              { value: '8:00 AM', label: '8:00 AM' },
              { value: '8:30 AM', label: '8:30 AM' },
              { value: '9:00 AM', label: '9:00 AM' },
              { value: '9:30 AM', label: '9:30 AM' },
              { value: '10:00 AM', label: '10:00 AM' },
              { value: '10:30 AM', label: '10:30 AM' },
              { value: '11:00 AM', label: '11:00 AM' },
              { value: '11:30 AM', label: '11:30 AM' },
              { value: '12:00 PM', label: '12:00 PM' },
              { value: '12:30 PM', label: '12:30 PM' },
              { value: '1:00 PM', label: '1:00 PM' },
              { value: '1:30 PM', label: '1:30 PM' },
              { value: '2:00 PM', label: '2:00 PM' },
              { value: '2:30 PM', label: '2:30 PM' },
              { value: '3:00 PM', label: '3:00 PM' },
              { value: '3:30 PM', label: '3:30 PM' },
              { value: '4:00 PM', label: '4:00 PM' },
              { value: '4:30 PM', label: '4:30 PM' },
              { value: '5:00 PM', label: '5:00 PM' },
              { value: '5:30 PM', label: '5:30 PM' },
              { value: '6:00 PM', label: '6:00 PM' },
              { value: '6:30 PM', label: '6:30 PM' },
              { value: '7:00 PM', label: '7:00 PM' },
              { value: '7:30 PM', label: '7:30 PM' },
              { value: '8:00 PM', label: '8:00 PM' },
              { value: '8:30 PM', label: '8:30 PM' },
              { value: '9:00 PM', label: '9:00 PM' },
              { value: '9:30 PM', label: '9:30 PM' },
              { value: '10:00 PM', label: '10:00 PM' },
              { value: '10:30 PM', label: '10:30 PM' },
              { value: '11:00 PM', label: '11:00 PM' },
              { value: '11:30 PM', label: '11:30 PM' },
            ]}
          />

          <TextField
            name="duration"
            label={(
              <>
                <span>Duration</span>
                <abbr title="required">*</abbr>
              </>
            )}
          />

          <SelectFieldV2
            name="time_zone"
            label={(
              <>
                <span>Time Zone</span>
                <abbr title="required">*</abbr>
              </>
            )}
            placeholder={timeZones === null ? 'Loading...' : 'Choose Time Zone...'}
            options={timeZones === null ? [] : timeZones}
          />

          <DisableTextField
            name="host"
            label={(
              <>
                <span>Host</span>
                <abbr title="required">*</abbr>
              </>
            )}
          />

          <div className="text-center mt50 mb30">
            <CancelPopupButton
              className="btn btn-primary btn-outline mr5 ml5 width-120"
              title="Cancel"
              onClick={onHide}
            />

            <SubmitButton
              title={isSubmitting ? 'Please, wait ...' : 'Update'}
              className={`btn width-120 btn-primary submit-button mr5 ml5 ${
                isSubmitting ? 'not-possible-click' : ''
              }`}
            />
          </div>
        </FormikForm>
      )}
    </Formik>
  );
}

Form.propTypes = {
  row: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
};
